import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { axiosDelete, axiosGet, axiosPost, axiosPut } from '../api/axios';

export type FiScriptLineRow = {
  id: number;
  response_key: string;
  body: string;
  sort_order: number;
  is_active: boolean;
  created_at?: string;
  updated_at?: string;
};

export const fiScriptLinesQueryKey = ['fiScriptLines', 'list'] as const;
export const fiScriptLinesMetaQueryKey = ['fiScriptLines', 'meta'] as const;

export type FiScriptLinesMeta = {
  response_keys: string[];
  tool_key_map: Record<string, string[]>;
};

export function useFiScriptLinesMeta() {
  return useQuery({
    queryKey: fiScriptLinesMetaQueryKey,
    queryFn: async () => {
      const res = await axiosGet('/fi_script_lines/meta', {}, 'v3');
      return res.data as FiScriptLinesMeta;
    },
  });
}

export function useFiScriptLinesList() {
  return useQuery({
    queryKey: fiScriptLinesQueryKey,
    queryFn: async () => {
      const res = await axiosGet('/fi_script_lines/', {}, 'v3');
      return (res.data as { data: FiScriptLineRow[] }).data;
    },
  });
}

export function useCreateFiScriptLine() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: Omit<FiScriptLineRow, 'id' | 'created_at' | 'updated_at'>) => {
      const res = await axiosPost('/fi_script_lines/', payload, 'v3');
      return res.data as FiScriptLineRow;
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: fiScriptLinesQueryKey });
    },
  });
}

export function useUpdateFiScriptLine() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...payload }: Partial<FiScriptLineRow> & { id: number }) => {
      const res = await axiosPut(`/fi_script_lines/${id}`, payload, 'v3');
      return res.data as FiScriptLineRow;
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: fiScriptLinesQueryKey });
    },
  });
}

export function useDeleteFiScriptLine() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => {
      await axiosDelete(`/fi_script_lines/${id}`, {}, 'v3');
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: fiScriptLinesQueryKey });
    },
  });
}
